import { useHistory } from "react-router";

import { toastError } from "@alpacahq/ui/old";

import { useGetOptionContracts } from "~/v2/api/hooks";
import type { Asset } from "~/v2/api/rest";
import type { Account, Position, Product } from "~/v2/api/rest/account";
import type { OptionContract, Quote } from "~/v2/api/rest/data";
import { OptionsPromoCard } from "~/v2/components/marketing/OptionsPromoCard";
import { OrderTicket } from "~/v2/components/orderTicket";
import { Path } from "~/v2/path";
import { getPrettyAmount } from "~/v2/utils/formatting";
import { useIsOptionsOnboardingAvailable } from "../../optionsOptIn/util";
import { CloseContract } from "./CloseContract";
import { ExerciseContract } from "./ExerciseContract";

type ColumnAccessorProps = Pick<
  OptionContract,
  "close_price" | "strike_price" | "type"
>;

const getBreakeven = ({
  close_price,
  strike_price,
  type,
}: ColumnAccessorProps) => {
  const closePrice = parseFloat(close_price ?? "0");
  if (type === "call") {
    return getPrettyAmount(parseFloat(strike_price) + closePrice);
  }

  return getPrettyAmount(parseFloat(strike_price) - closePrice);
};

const getBreakevenPercent = ({
  price,
  type,
  close_price,
  strike_price,
}: ColumnAccessorProps & { price: number }) =>
  `${(
    ((parseFloat(getBreakeven({ type, close_price, strike_price })) - price) /
      price) *
      100
  ).toFixed(2)}%`;

interface ContractDetailProps {
  contractSymbol?: string;
  quote: Quote | undefined;
  product?: Product;
  asset: Asset;
  positions: Position[];
  account: Account;
}

export const ContractDetail = ({
  contractSymbol,
  asset,
  quote,
  product,
  positions,
  account,
}: ContractDetailProps) => {
  const history = useHistory();

  const { isOptionsApproved } = useIsOptionsOnboardingAvailable();

  const { data: { option_contracts: optionContracts } = {} } =
    useGetOptionContracts({
      assetSymbol: asset?.symbol,
      options: { enabled: !!asset },
    });

  const underlyingSymbol = contractSymbol?.substring(
    0,
    contractSymbol?.search(/\d/),
  );

  const position = positions.find(({ symbol }) => symbol === contractSymbol);
  const underlyingPositions = positions.filter(
    ({ symbol }) => symbol === underlyingSymbol,
  );

  const contract = optionContracts?.find(
    (contract) => (position?.symbol ?? contractSymbol) === contract.symbol,
  );

  const onBack = () => {
    // Send the user back to the option chain
    if (underlyingSymbol) {
      history.push(Path.format(Path.ROUTE_TRADE, { symbol: underlyingSymbol }));
    } else {
      toastError("underlying symbol not found");
    }
  };

  return (
    <div className="flex-1">
      <div className="mb-6 flex items-center justify-between gap-2">
        <button
          type="button"
          // @todo This is just a temporary styling (we're moving fast OK?)
          className="flex h-10 cursor-pointer items-center justify-center rounded-lg bg-zinc-100 px-4 font-medium text-sm transition-all duration-400 ease-in-out hover:bg-zinc-200"
          onClick={onBack}
        >
          ← &nbsp;Back to {underlyingSymbol}
        </button>
        <div className="flex gap-2">
          {!!position && (
            <CloseContract
              product={product}
              account={account}
              contract={contract}
              position={position}
            />
          )}
          {!!position && parseInt(position?.qty ?? "0", 10) > 0 && (
            <ExerciseContract
              product={product}
              account={account}
              contract={contract}
              position={position}
              underlyingPositions={underlyingPositions}
            />
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-6">
        <div className="flex-1 space-y-4 overflow-x-auto">
          <div className="w-full">
            <span className="mb-4 block border-zinc-200 border-b pb-4 font-medium text-lg">
              Position
            </span>

            <div className="grid grid-cols-1 gap-1 gap-x-6 lg:grid-cols-2">
              {position ? (
                [
                  { label: "Quantity", value: position?.qty },
                  {
                    label: "Average Entry Price",
                    value: getPrettyAmount(position?.avg_entry_price),
                  },
                  {
                    label: "Current Price",
                    value: getPrettyAmount(position?.current_price),
                  },
                  {
                    label: "Market Value",
                    value: getPrettyAmount(position?.market_value),
                  },
                  {
                    label: "Cost Basis",
                    value: getPrettyAmount(position?.cost_basis),
                  },
                  {
                    label: "Unrealized P/L",
                    value: `${getPrettyAmount(position?.unrealized_pl)} (${(
                      parseFloat(position?.unrealized_plpc || "0") * 100
                    ).toFixed(2)}%)`,
                  },
                  {
                    label: "Change Today",
                    value: `${(
                      parseFloat(position?.change_today || "0") * 100
                    ).toFixed(2)}%`,
                  },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="flex justify-between border-zinc-100 border-b pb-1"
                  >
                    <span>{item.label}</span>
                    <span className="text-zinc-500">{item.value}</span>
                  </div>
                ))
              ) : (
                <div className="text-zinc-500">
                  Place a trade to see your position details.
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-4 md:flex-row">
            <div className="w-full">
              <span className="mb-4 block border-zinc-200 border-b pb-4 font-medium text-lg">
                Details
              </span>
              <div className="grid grid-cols-1 gap-1 gap-x-6 lg:grid-cols-2">
                {[
                  { label: "Contract", value: contract?.symbol },
                  { label: "Expiration", value: contract?.expiration_date },
                  {
                    label: "Underlying",
                    value: contract?.underlying_symbol,
                  },

                  { label: "Strike", value: contract?.strike_price },
                  {
                    label: "Underlying Price",
                    value: getPrettyAmount(quote?.last),
                  },
                  { label: "Type", value: contract?.type },
                  { label: "Style", value: contract?.style },
                  { label: "Size", value: contract?.size },
                  {
                    label: "Last",
                    value: getPrettyAmount(contract?.close_price),
                  },
                  {
                    label: "OI",
                    value: getPrettyAmount(contract?.open_interest),
                  },
                  {
                    label: "Breakeven",
                    value: getBreakeven({
                      close_price: contract?.close_price || "0",
                      strike_price: contract?.strike_price || "0",
                      type: contract?.type || "call",
                    }),
                  },
                  {
                    label: "Breakeven %",
                    value: getBreakevenPercent({
                      price: quote?.last || 0,
                      close_price: contract?.close_price || "0",
                      strike_price: contract?.strike_price || "0",
                      type: contract?.type || "call",
                    }),
                  },
                  { label: "Open Interest", value: contract?.open_interest },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="flex justify-between border-zinc-100 border-b pb-1"
                  >
                    <span>{item.label}</span>
                    <span className="text-zinc-500">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="w-full md:w-[368px]">
          {!isOptionsApproved ? (
            <OptionsPromoCard />
          ) : (
            <OrderTicket symbol={contractSymbol} />
          )}
        </div>
      </div>
    </div>
  );
};
